import { computed, ref } from 'vue';
import { formatDateForApi } from '@/shared/lib';
import { type CreateOpenTrainingExternalCompanyInstructorPayload } from '@/entities/Training';
import { useCurriculumUploadRow, type CurriculumUploadStatus } from './useCurriculumUploadRow';
import { useNameTrainingDateRow } from './useNameTrainingDateRow';

export const MAX_EXTERNAL_INSTRUCTORS = 5;
export const MIN_EXTERNAL_INSTRUCTORS = 1;

export interface ExternalCompanyInstructorRow {
  id: number;
  name: string;
  trainingDate: Date | null;
  curriculumObjectName: string | null;
  curriculumFileName: string | null;
  uploadProgress: number;
  uploadStatus: CurriculumUploadStatus;
  nameError: string | null;
}

const ROW_FIELD_ERROR_PATTERN = /^externalCompanyInstructors\.(\d+)\.name$/;

export interface ExternalCompanyInstructorsPayload {
  cnpj: string;
  companyName: string;
  externalCompanyInstructors: CreateOpenTrainingExternalCompanyInstructorPayload[];
}

export function useOpenTrainingExternalCompanyInstructors() {
  let nextRowId = 1;

  function createEmptyRow(): ExternalCompanyInstructorRow {
    return {
      id: nextRowId++,
      name: '',
      trainingDate: null,
      curriculumObjectName: null,
      curriculumFileName: null,
      uploadProgress: 0,
      uploadStatus: 'idle',
      nameError: null,
    };
  }

  const cnpj = ref('');
  const companyName = ref('');
  const cnpjError = ref<string | null>(null);
  const companyNameError = ref<string | null>(null);
  const rows = ref<ExternalCompanyInstructorRow[]>([createEmptyRow()]);
  const curriculumUpload = useCurriculumUploadRow(rows);
  const nameTrainingDateRow = useNameTrainingDateRow(rows);

  const canAddMore = computed(() => rows.value.length < MAX_EXTERNAL_INSTRUCTORS);
  const canRemove = computed(() => rows.value.length > MIN_EXTERNAL_INSTRUCTORS);

  const isComplete = computed(
    () =>
      cnpj.value.trim() !== '' &&
      companyName.value.trim() !== '' &&
      rows.value.every(
        (row) => row.name.trim() !== '' && row.trainingDate !== null && row.uploadStatus !== 'uploading'
      )
  );

  function addRow(): void {
    if (rows.value.length < MAX_EXTERNAL_INSTRUCTORS) {
      rows.value.push(createEmptyRow());
    }
  }

  function removeRow(index: number): void {
    if (rows.value.length > MIN_EXTERNAL_INSTRUCTORS) {
      rows.value.splice(index, 1);
    }
  }

  function reset(): void {
    cnpj.value = '';
    companyName.value = '';
    cnpjError.value = null;
    companyNameError.value = null;
    rows.value = [createEmptyRow()];
  }

  function setCnpj(value: string): void {
    cnpj.value = value;
    cnpjError.value = null;
  }

  function setCompanyName(value: string): void {
    companyName.value = value;
    companyNameError.value = null;
  }

  function applyFieldErrors(fieldErrors: Record<string, string[]>): void {
    cnpjError.value = null;
    companyNameError.value = null;
    for (const row of rows.value) {
      row.nameError = null;
    }

    for (const [path, messages] of Object.entries(fieldErrors)) {
      const message = messages[0];
      if (!message) continue;

      if (path === 'cnpj') {
        cnpjError.value = message;
        continue;
      }
      if (path === 'companyName') {
        companyNameError.value = message;
        continue;
      }

      const match = ROW_FIELD_ERROR_PATTERN.exec(path);
      if (!match) continue;
      const row = rows.value[Number(match[1])];
      if (row) row.nameError = message;
    }
  }

  function buildPayload(): ExternalCompanyInstructorsPayload | null {
    if (!cnpj.value.trim() || !companyName.value.trim()) return null;

    const externalCompanyInstructors: CreateOpenTrainingExternalCompanyInstructorPayload[] = [];

    for (const row of rows.value) {
      if (!row.name.trim() || row.trainingDate === null) return null;
      const trainingDate = formatDateForApi(row.trainingDate);
      if (trainingDate === null) return null;

      externalCompanyInstructors.push({
        name: row.name.trim(),
        trainingDate,
        curriculumObjectName: row.curriculumObjectName ?? undefined,
      });
    }

    return {
      cnpj: cnpj.value.replace(/\D/g, ''),
      companyName: companyName.value.trim(),
      externalCompanyInstructors,
    };
  }

  return {
    cnpj,
    companyName,
    cnpjError,
    companyNameError,
    rows,
    canAddMore,
    canRemove,
    isComplete,
    addRow,
    removeRow,
    reset,
    setCnpj,
    setCompanyName,
    setName: nameTrainingDateRow.setName,
    setTrainingDate: nameTrainingDateRow.setTrainingDate,
    selectCurriculumFile: curriculumUpload.selectCurriculumFile,
    removeCurriculumFile: curriculumUpload.removeCurriculumFile,
    buildPayload,
    applyFieldErrors,
  };
}
